import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Main } from '@/components/layout/main'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import { toast } from '@/hooks/use-toast'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription,
} from '@/components/ui/card'
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select'
import {
  IconUser, IconPhone, IconMapPin, IconCertificate,
  IconSchool, IconLanguage, IconBriefcase, IconEye, IconTrash, IconPlus,
} from '@tabler/icons-react'
import {
  get_lawyer_profile, create_lawyer_profile, update_lawyer_profile,
  delete_lawyer_profile, LawyerProfile, LawyerProfilePayload, Education, Certification, CareerEntry,
} from '@/api/lawyer-profile'
import { DeleteDialog } from '@/components/delete-dialog'
import { ConfirmDialog } from '@/components/confirm-dialog'
import ProfilePhotoUploader, { ProfilePhotoUploaderRef } from '@/components/lawyer-profile/ProfilePhotoUploader'
import { LawyerProfilePreview } from './preview'
import { usePermission } from '@/hooks/use-permission'
import { UserPermission } from '@/constants/permissions'
import { IconLock } from '@tabler/icons-react'

import CITIES_DATA from '@/assets/data/cities.json'
import LANGUAGES_DATA from '@/assets/data/languages.json'
import SPECIALIZATIONS_DATA from '@/assets/data/specializations.json'

// ── Static data ───────────────────────────────────────────────────────────────
interface CityOption   { value: string; ar: string; fr: string }
interface LangOption   { value: string; ar: string; fr: string }
interface SpecOption   { key: string;   ar: string; fr: string }

const CITIES:          CityOption[] = CITIES_DATA as CityOption[]
const LANGUAGES:       LangOption[] = LANGUAGES_DATA as LangOption[]
const SPECIALIZATIONS: SpecOption[] = SPECIALIZATIONS_DATA as SpecOption[]

const CURRENT_YEAR = new Date().getFullYear()
const YEARS  = Array.from({ length: CURRENT_YEAR - 1969 }, (_, i) => CURRENT_YEAR - i)
const MONTHS = Array.from({ length: 12 }, (_, i) => i + 1)

// ── Validation ────────────────────────────────────────────────────────────────
const NAME_REGEX  = /^[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FFa-zA-ZÀ-ÿ\s'-]+$/
const PHONE_REGEX = /^(\+212|0)([ \-]?)([5-7]\d{8})$/
const URL_REGEX   = /^https:\/\/.+/

interface FormErrors {
  full_name?:       string
  title?:           string
  bio_ar?:          string
  bio_fr?:          string
  phone?:           string
  whatsapp?:        string
  email_public?:    string
  website?:         string
  linkedin?:        string
  google_maps_url?: string
  career?:          string[]
}

function validate(form: Partial<LawyerProfilePayload>, t: (k: string) => string): FormErrors {
  const errors: FormErrors = {}

  if (!form.full_name || form.full_name.trim().length < 2)
    errors.full_name = t('error_name_min')
  else if (form.full_name.trim().length > 80)
    errors.full_name = t('error_name_max')
  else if (!NAME_REGEX.test(form.full_name.trim()))
    errors.full_name = t('error_name_chars')

  if (form.title && form.title.length > 50)
    errors.title = t('error_title_max')

  if (form.bio_ar && form.bio_ar.length > 500)
    errors.bio_ar = t('error_bio_max')
  if (form.bio_fr && form.bio_fr.length > 500)
    errors.bio_fr = t('error_bio_max')

  if (form.phone && !PHONE_REGEX.test(form.phone.replace(/\s/g, '')))
    errors.phone = t('error_phone_format')
  if (form.whatsapp && !PHONE_REGEX.test(form.whatsapp.replace(/\s/g, '')))
    errors.whatsapp = t('error_phone_format')

  if (form.email_public && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email_public))
    errors.email_public = t('error_email_format')

  if (form.website && !URL_REGEX.test(form.website))
    errors.website = t('error_url_https')
  if (form.linkedin && !URL_REGEX.test(form.linkedin))
    errors.linkedin = t('error_url_https')
  if (form.google_maps_url && !URL_REGEX.test(form.google_maps_url))
    errors.google_maps_url = t('error_url_https')

  const careerErrors: string[] = []
  ;(form.career || []).forEach((entry, i) => {
    if (!entry.current && entry.to) {
      const fromVal = (entry.from * 100) + (entry.from_month ?? 0)
      const toVal   = (entry.to   * 100) + (entry.to_month   ?? 12)
      if (fromVal > toVal) careerErrors[i] = t('error_career_dates')
    }
  })
  if (careerErrors.some(Boolean)) errors.career = careerErrors

  return errors
}

// ── Field error ───────────────────────────────────────────────────────────────
function FieldError({ msg }: { msg?: string }) {
  if (!msg) return null
  return <p className="text-xs text-destructive mt-1">{msg}</p>
}

// ── City dropdown ─────────────────────────────────────────────────────────────
function CitySelect({ value, onChange, placeholder }: {
  value: string; onChange: (val: string) => void; placeholder?: string
}) {
  const { i18n } = useTranslation()
  const isAr = i18n.language === 'ar'
  return (
    <Select value={value || ''} onValueChange={onChange}>
      <SelectTrigger>
        <SelectValue placeholder={placeholder || '—'} />
      </SelectTrigger>
      <SelectContent>
        {CITIES.map((c) => (
          <SelectItem key={c.value} value={c.value}>
            {isAr ? c.ar : c.fr}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

// ── Section Toggle ────────────────────────────────────────────────────────────
function SectionToggle({ label, checked, onChange }: {
  label: string; checked: boolean; onChange: (val: boolean) => void
}) {
  return (
    <div className="flex items-center gap-2" dir="ltr">
      <Switch checked={checked} onCheckedChange={onChange} />
      <span className="text-xs text-muted-foreground">{label}</span>
    </div>
  )
}

// ── Section Card ──────────────────────────────────────────────────────────────
function SectionCard({ icon, title, showValue, onShowChange, children }: {
  icon: React.ReactNode; title: string; showValue: boolean
  onShowChange: (val: boolean) => void; children: React.ReactNode
}) {
  const { t } = useTranslation()
  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="text-primary">{icon}</div>
            <CardTitle className="text-base">{title}</CardTitle>
          </div>
          <SectionToggle label={t('show_section')} checked={showValue} onChange={onShowChange} />
        </div>
      </CardHeader>
      <CardContent className="space-y-4">{children}</CardContent>
    </Card>
  )
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function LawyerProfilePage() {
  const { t, i18n } = useTranslation()
  const isAr = i18n.language === 'ar'
  const { can } = usePermission()

  const canView   = can(UserPermission.VIEW_LAWYER_PROFILE)
  const canCreate = can(UserPermission.ADD_LAWYER_PROFILE)
  const canEdit   = can(UserPermission.CHANGE_LAWYER_PROFILE)
  const canDelete = can(UserPermission.DELETE_LAWYER_PROFILE)

  const [loading, setLoading]           = useState(true)
  const [saving, setSaving]             = useState(false)
  const [deleteOpen, setDeleteOpen]     = useState(false)
  const [previewing, setPreviewing]     = useState(false)
  const [publishConfirm, setPublishConfirm] = useState<boolean | null>(null)
  const [errors, setErrors]             = useState<FormErrors>({})
  const [profile, setProfile]           = useState<LawyerProfile | null>(null)
  const photoRef = useRef<ProfilePhotoUploaderRef>(null)

  const [form, setForm] = useState<Partial<LawyerProfilePayload>>({
    full_name: '',full_name_fr: '',
    title: '', bar_city: '',rattachement_year: null,
    bio_ar: '', bio_fr: '', show_bio: true,
    phone: '', whatsapp: '', email_public: '', website: '',
    linkedin: '', google_maps_url: '', show_contact: true,
    cabinet_address: '', cabinet_city: '', cabinet_country: 'Maroc',
    specializations: [], show_specializations: true, show_experience: true,
    education: [], show_education: true,
    certifications: [], show_certifications: true,
    career: [], show_career: true,
    languages_spoken: [], show_languages: true,
    is_published: false,
  })

  useEffect(() => {
    get_lawyer_profile()
      .then((data) => {
        if (data) {
          setProfile(data)
          setForm({
            full_name: data.full_name,full_name_fr: data.full_name_fr,
            rattachement_year: data.rattachement_year,
            title: data.title || '', bar_city: data.bar_city,
            bio_ar: data.bio_ar, bio_fr: data.bio_fr, show_bio: data.show_bio,
            phone: data.phone, whatsapp: data.whatsapp,
            email_public: data.email_public ?? '', website: data.website,
            linkedin: data.linkedin, google_maps_url: data.google_maps_url,
            show_contact: data.show_contact,
            cabinet_address: data.cabinet_address, cabinet_city: data.cabinet_city,
            cabinet_country: data.cabinet_country,
            specializations: data.specializations, show_specializations: data.show_specializations
            , show_experience: data.show_experience,
            education: data.education, show_education: data.show_education,
            certifications: data.certifications, show_certifications: data.show_certifications,
            career: data.career, show_career: data.show_career,
            languages_spoken: data.languages_spoken, show_languages: data.show_languages,
            is_published: data.is_published, photo_path: data.photo_path ?? undefined,
          })
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [])

  const set = (key: keyof LawyerProfilePayload, value: unknown) =>
    setForm((prev) => ({ ...prev, [key]: value }))

  const addEducation = () =>
    set('education', [...(form.education || []), { degree: '', institution: '', year: CURRENT_YEAR }])
  const updateEducation = (i: number, field: keyof Education, value: string | number) => {
    const u = [...(form.education || [])]; u[i] = { ...u[i], [field]: value }; set('education', u)
  }
  const removeEducation = (i: number) =>
    set('education', (form.education || []).filter((_, idx) => idx !== i))

  const addCertification = () =>
    set('certifications', [...(form.certifications || []), { title: '', issuer: '', year: CURRENT_YEAR }])
  const updateCertification = (i: number, field: keyof Certification, value: string | number) => {
    const u = [...(form.certifications || [])]; u[i] = { ...u[i], [field]: value }; set('certifications', u)
  }
  const removeCertification = (i: number) =>
    set('certifications', (form.certifications || []).filter((_, idx) => idx !== i))

  const addCareer = () =>
    set('career', [...(form.career || []), {
      role: '', cabinet: '', city: '',
      from: CURRENT_YEAR, from_month: null,
      to: null, to_month: null, current: false,
    }])
  const updateCareer = (i: number, field: keyof CareerEntry, value: string | number | boolean | null) => {
    const u = [...(form.career || [])]; u[i] = { ...u[i], [field]: value }; set('career', u)
  }
  const removeCareer = (i: number) =>
    set('career', (form.career || []).filter((_, idx) => idx !== i))

  const toggleSpecialization = (spec: SpecOption) => {
    const current = form.specializations || []
    const exists = current.find((s) => s.key === spec.key)
    if (exists) set('specializations', current.filter((s) => s.key !== spec.key))
    else set('specializations', [...current, { key: spec.key, ar: spec.ar, fr: spec.fr }])
  }
  const isSpecSelected = (key: string) => (form.specializations || []).some((s) => s.key === key)

  const toggleLanguage = (code: string) => {
    const current = form.languages_spoken || []
    if (current.includes(code)) set('languages_spoken', current.filter((l) => l !== code))
    else set('languages_spoken', [...current, code])
  }

  const handleSave = async () => {
    const errs = validate(form, t)
    if (Object.keys(errs).length > 0) {
      setErrors(errs)
      toast({ title: t('error_form_invalid'), variant: 'destructive' })
      return
    }
    setErrors({})
    setSaving(true)
    try {
      let updatedForm = { ...form }
      if (photoRef.current) {
        const photoResult = await photoRef.current.uploadImage()
        if (photoResult) updatedForm = { ...updatedForm, photo_path: photoResult.photo_path }
      }
      if (profile) {
        const updated = await update_lawyer_profile(updatedForm)
        setProfile(updated)
      } else {
        const created = await create_lawyer_profile(updatedForm)
        setProfile(created)
      }
      toast({ title: t('saved_successfully') })
    } catch {
      toast({ title: t('error'), variant: 'destructive' })
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <Main>
        <div className="space-y-4 max-w-3xl mx-auto">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-48 w-full" />
          <Skeleton className="h-48 w-full" />
        </div>
      </Main>
    )
  }

  // Access denied: user cannot view lawyer profiles
  if (!canView) {
    return (
      <Main>
        <div className="max-w-3xl mx-auto flex flex-col items-center justify-center py-24 gap-4 text-center">
          <IconLock size={48} className="text-muted-foreground" />
          <h2 className="text-xl font-semibold">{t('access_denied')}</h2>
          <p className="text-sm text-muted-foreground">{t('no_permission_to_view')}</p>
        </div>
      </Main>
    )
  }

  return (
    <Main>
      <div className="max-w-3xl mx-auto space-y-6">

        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h2 className="text-2xl font-bold tracking-tight">{t('lawyer_profile')}</h2>
            <p className="text-muted-foreground text-sm">{t('lawyer_profile_desc')}</p>
          </div>
          <div className="flex items-center gap-2">
            {profile && canDelete && (
              <Button variant="destructive" size="sm" onClick={() => setDeleteOpen(true)}>
                <IconTrash size={16} className="mr-1" />{t('delete')}
              </Button>
            )}
            <Button variant="outline" onClick={() => setPreviewing(true)} className="gap-1">
              <IconEye size={16} />{t('preview')}
            </Button>
            {(canEdit || (!profile && canCreate)) && (
              <Button onClick={handleSave} disabled={saving}>
                {saving ? t('saving') : t('save')}
              </Button>
            )}
          </div>
        </div>

        {/* Publish toggle */}
        <Card className={form.is_published ? 'border-primary/50 bg-primary/5' : ''}>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <IconEye size={20} className="text-primary" />
                <div>
                  <p className="font-medium text-sm">{t('publish_profile')}</p>
                  <p className="text-xs text-muted-foreground">{t('publish_profile_desc')}</p>
                </div>
              </div>
              <div dir="ltr">
                <Switch checked={form.is_published} onCheckedChange={(val) => setPublishConfirm(val)} />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Identity */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <IconUser size={18} className="text-primary" />
              <CardTitle className="text-base">{t('identity')}</CardTitle>
            </div>
            <CardDescription className="text-xs">{t('identity_desc')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex justify-center pb-2">
              <ProfilePhotoUploader ref={photoRef} initialImage={profile?.photo_url}
                onUploaded={(_, path) => set('photo_path', path)} />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>{t('full_name')} <span className="text-destructive">*</span></Label>
                <Input value={form.full_name} maxLength={80}
                  onChange={(e) => set('full_name', e.target.value)}
                  className={errors.full_name ? 'border-destructive' : ''} />
                <div className="flex justify-between items-center">
                  <FieldError msg={errors.full_name} />
                  <span className="text-xs text-muted-foreground ms-auto">{(form.full_name || '').length}/80</span>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>{t('title')}</Label>
                <Input value={form.title} maxLength={50} placeholder="Maître,Avocat,Dr..."
                  onChange={(e) => set('title', e.target.value)}
                  className={errors.title ? 'border-destructive' : ''} />
                <FieldError msg={errors.title} />
              </div>
              <div className="space-y-1.5">
                <Label>{t('bar_city')}</Label>
                <CitySelect value={form.bar_city || ''} onChange={(val) => set('bar_city', val)} placeholder={t('select_city')} />
              </div>
              <div className="space-y-1.5">
                <Label>{t('full_name_fr')}</Label>
                <Input
                  value={form.full_name_fr}
                  onChange={(e) => set('full_name_fr', e.target.value)}
                  placeholder=""
                />
              </div>
              <div className="space-y-1.5">
                <Label>{t('rattachement_year')}</Label>
                <Select
                  value={form.rattachement_year ? String(form.rattachement_year) : '0'}
                  onValueChange={(v) => set('rattachement_year', v === '0' ? null : Number(v))}
                >
                  <SelectTrigger className="w-36">
                    <SelectValue placeholder="—" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">—</SelectItem>
                    {YEARS.map((y) => (
                      <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Bio */}
        <SectionCard icon={<IconUser size={18} />} title={t('bio')}
          showValue={form.show_bio!} onShowChange={(val) => set('show_bio', val)}>
          <div className="space-y-1.5">
            <Label>{t('bio_ar')}</Label>
            <Textarea dir="rtl" rows={3} value={form.bio_ar} maxLength={500}
              onChange={(e) => set('bio_ar', e.target.value)} placeholder="نبذة عن المحامي..."
              className={errors.bio_ar ? 'border-destructive' : ''} />
            <div className="flex justify-between items-center">
              <FieldError msg={errors.bio_ar} />
              <span className="text-xs text-muted-foreground ms-auto">{(form.bio_ar || '').length}/500</span>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>{t('bio_fr')}</Label>
            <Textarea rows={3} value={form.bio_fr} maxLength={500}
              onChange={(e) => set('bio_fr', e.target.value)} placeholder="À propos de l'avocat..."
              className={errors.bio_fr ? 'border-destructive' : ''} />
            <div className="flex justify-between items-center">
              <FieldError msg={errors.bio_fr} />
              <span className="text-xs text-muted-foreground ms-auto">{(form.bio_fr || '').length}/500</span>
            </div>
          </div>
        </SectionCard>

        {/* Contact */}
        <SectionCard icon={<IconPhone size={18} />} title={t('contact')}
          showValue={form.show_contact!} onShowChange={(val) => set('show_contact', val)}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>{t('phone')}</Label>
              <Input value={form.phone} placeholder="+212600000000"
                onChange={(e) => set('phone', e.target.value)}
                className={errors.phone ? 'border-destructive' : ''} />
              <FieldError msg={errors.phone} />
            </div>
            <div className="space-y-1.5">
              <Label>{t('whatsapp')}</Label>
              <Input value={form.whatsapp} placeholder="+212600000000"
                onChange={(e) => set('whatsapp', e.target.value)}
                className={errors.whatsapp ? 'border-destructive' : ''} />
              <FieldError msg={errors.whatsapp} />
            </div>
            <div className="space-y-1.5">
              <Label>{t('email_public')}</Label>
              <Input type="email" value={form.email_public ?? ''}
                onChange={(e) => set('email_public', e.target.value)}
                className={errors.email_public ? 'border-destructive' : ''} />
              <FieldError msg={errors.email_public} />
            </div>
            <div className="space-y-1.5">
              <Label>{t('website')}</Label>
              <Input value={form.website} placeholder="https://..."
                onChange={(e) => set('website', e.target.value)}
                className={errors.website ? 'border-destructive' : ''} />
              <FieldError msg={errors.website} />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label>{t('linkedin')}</Label>
              <Input value={form.linkedin} placeholder="https://linkedin.com/in/..."
                onChange={(e) => set('linkedin', e.target.value)}
                className={errors.linkedin ? 'border-destructive' : ''} />
              <FieldError msg={errors.linkedin} />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label>{t('google_maps_url')}</Label>
              <Input value={form.google_maps_url} placeholder="https://maps.google.com/..."
                onChange={(e) => set('google_maps_url', e.target.value)}
                className={errors.google_maps_url ? 'border-destructive' : ''} />
              <FieldError msg={errors.google_maps_url} />
            </div>
          </div>
        </SectionCard>

        {/* Cabinet Location */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <IconMapPin size={18} className="text-primary" />
              <CardTitle className="text-base">{t('cabinet_location')}</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-1.5">
              <Label>{t('cabinet_address')}</Label>
              <Textarea rows={2} value={form.cabinet_address} onChange={(e) => set('cabinet_address', e.target.value)} />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>{t('city')}</Label>
                <CitySelect value={form.cabinet_city || ''} onChange={(val) => set('cabinet_city', val)} placeholder={t('select_city')} />
              </div>
              <div className="space-y-1.5">
                <Label>{t('country')}</Label>
                <Input value={form.cabinet_country} onChange={(e) => set('cabinet_country', e.target.value)} />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Specializations */}
        <SectionCard icon={<IconBriefcase size={18} />} title={t('specializations')}
          showValue={form.show_specializations!} onShowChange={(val) => set('show_specializations', val)}>
          <div className="flex flex-wrap gap-2">
            {SPECIALIZATIONS.map((spec) => {
              const selected = isSpecSelected(spec.key)
              return (
                <button key={spec.key} type="button" onClick={() => toggleSpecialization(spec)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                    selected ? 'bg-primary text-primary-foreground border-primary'
                      : 'bg-background text-muted-foreground border-border hover:border-primary/50'}`}>
                  {isAr ? spec.ar : spec.fr}
                </button>
              )
            })}
          </div>
          {(form.specializations || []).length > 0 && (
            <p className="text-xs text-muted-foreground">
              {(form.specializations || []).map((s) => isAr ? s.ar : s.fr).join(' · ')}
            </p>
          )}
        </SectionCard>
        {/* Experience */}
        <SectionCard icon={<IconBriefcase size={18} />} title={t('experience')}
          showValue={form.show_experience!} onShowChange={(val) => set('show_experience', val)}>
          {form.rattachement_year ? (
            <div className="flex items-center gap-2 p-3 rounded-lg bg-muted/40 border border-border/50">
              <IconBriefcase size={16} className="text-primary" />
              <span className="text-sm font-medium">
                {new Date().getFullYear() - form.rattachement_year} {t('years')}
              </span>
              <span className="text-xs text-muted-foreground">
                ({t('since')} {form.rattachement_year})
              </span>
            </div>
          ) : (
            <p className="text-xs text-muted-foreground italic">
              {t('experience_requires_rattachement_year')}
            </p>
          )}
        </SectionCard>

        {/* Education */}
        <SectionCard icon={<IconSchool size={18} />} title={t('education')}
          showValue={form.show_education!} onShowChange={(val) => set('show_education', val)}>
          <div className="space-y-3">
            {(form.education || []).map((edu, i) => (
              <div key={i} className="grid grid-cols-1 sm:grid-cols-3 gap-3 p-3 rounded-lg border border-border/60 bg-muted/30">
                <div className="space-y-1">
                  <Label className="text-xs">{t('degree')}</Label>
                  <Input value={edu.degree} onChange={(e) => updateEducation(i, 'degree', e.target.value)} placeholder="Master, Licence..." />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">{t('institution')}</Label>
                  <Input value={edu.institution} onChange={(e) => updateEducation(i, 'institution', e.target.value)} placeholder="Université..." />
                </div>
                <div className="flex gap-2 items-end">
                  <div className="flex-1 space-y-1">
                    <Label className="text-xs">{t('year')}</Label>
                    <Select value={String(edu.year)} onValueChange={(v) => updateEducation(i, 'year', Number(v))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {YEARS.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button variant="ghost" size="icon" className="text-destructive h-9 w-9" onClick={() => removeEducation(i)}>
                    <IconTrash size={16} />
                  </Button>
                </div>
              </div>
            ))}
            <Button variant="outline" size="sm" onClick={addEducation} className="gap-1">
              <IconPlus size={14} />{t('add_education')}
            </Button>
          </div>
        </SectionCard>

        {/* Certifications */}
        <SectionCard icon={<IconCertificate size={18} />} title={t('certifications')}
          showValue={form.show_certifications!} onShowChange={(val) => set('show_certifications', val)}>
          <div className="space-y-3">
            {(form.certifications || []).map((cert, i) => (
              <div key={i} className="grid grid-cols-1 sm:grid-cols-3 gap-3 p-3 rounded-lg border border-border/60 bg-muted/30">
                <div className="space-y-1">
                  <Label className="text-xs">{t('certification_title')}</Label>
                  <Input value={cert.title} onChange={(e) => updateCertification(i, 'title', e.target.value)} />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">{t('issuer')}</Label>
                  <Input value={cert.issuer} onChange={(e) => updateCertification(i, 'issuer', e.target.value)} />
                </div>
                <div className="flex gap-2 items-end">
                  <div className="flex-1 space-y-1">
                    <Label className="text-xs">{t('year')}</Label>
                    <Select value={String(cert.year)} onValueChange={(v) => updateCertification(i, 'year', Number(v))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {YEARS.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button variant="ghost" size="icon" className="text-destructive h-9 w-9" onClick={() => removeCertification(i)}>
                    <IconTrash size={16} />
                  </Button>
                </div>
              </div>
            ))}
            <Button variant="outline" size="sm" onClick={addCertification} className="gap-1">
              <IconPlus size={14} />{t('add_certification')}
            </Button>
          </div>
        </SectionCard>

        {/* Career */}
        <SectionCard icon={<IconBriefcase size={18} />} title={t('career')}
          showValue={form.show_career!} onShowChange={(val) => set('show_career', val)}>
          <div className="space-y-3">
            {(form.career || []).map((entry, i) => (
              <div key={i} className="space-y-3 p-3 rounded-lg border border-border/60 bg-muted/30">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">{t('role')}</Label>
                    <Input value={entry.role} onChange={(e) => updateCareer(i, 'role', e.target.value)} placeholder="Associé, Avocat..." />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">{t('cabinet')}</Label>
                    <Input value={entry.cabinet} onChange={(e) => updateCareer(i, 'cabinet', e.target.value)} placeholder="Cabinet A & Associés..." />
                  </div>
                  <div className="space-y-1 sm:col-span-2">
                    <Label className="text-xs">{t('city')}</Label>
                    <CitySelect value={entry.city || ''} onChange={(val) => updateCareer(i, 'city', val)} placeholder={t('select_city')} />
                  </div>
                </div>

                {/* From month/year */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">{t('from_month')}</Label>
                    <Select value={entry.from_month ? String(entry.from_month) : '0'}
                      onValueChange={(v) => updateCareer(i, 'from_month', v ? Number(v) : null)}>
                      <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="0">{t('no_month')}</SelectItem>
                        {MONTHS.map((m) => <SelectItem key={m} value={String(m)}>{m}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">{t('from_year')}</Label>
                    <Select value={String(entry.from)} onValueChange={(v) => updateCareer(i, 'from', Number(v))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {YEARS.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* To month/year */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">{t('to_month')}</Label>
                    <Select value={entry.current ? '0' : (entry.to_month ? String(entry.to_month) : '0')}
                      disabled={entry.current}
                      onValueChange={(v) => updateCareer(i, 'to_month', v ? Number(v) : null)}>
                      <SelectTrigger><SelectValue placeholder={entry.current ? t('present') : '—'} /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="0">{t('no_month')}</SelectItem>
                        {MONTHS.map((m) => <SelectItem key={m} value={String(m)}>{m}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">{t('to_year')}</Label>
                    <Select value={entry.current ? '0' : (entry.to ? String(entry.to) : '0')}
                      disabled={entry.current}
                      onValueChange={(v) => updateCareer(i, 'to', v === '0' ? null : Number(v))}>
                      <SelectTrigger><SelectValue placeholder={entry.current ? t('present') : '—'} /></SelectTrigger>
                      <SelectContent>
                          <SelectItem value="0">—</SelectItem> 
                        {YEARS.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {errors.career?.[i] && <p className="text-xs text-destructive">{errors.career[i]}</p>}

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2" dir="ltr">
                    <Switch checked={entry.current}
                      onCheckedChange={(val) => {
                        updateCareer(i, 'current', val)
                        if (val) { updateCareer(i, 'to', null); updateCareer(i, 'to_month', null) }
                      }} />
                    <Label className="text-xs">{t('current_position')}</Label>
                  </div>
                  <Button variant="ghost" size="icon" className="text-destructive h-9 w-9" onClick={() => removeCareer(i)}>
                    <IconTrash size={16} />
                  </Button>
                </div>
              </div>
            ))}
            <Button variant="outline" size="sm" onClick={addCareer} className="gap-1">
              <IconPlus size={14} />{t('add_career')}
            </Button>
          </div>
        </SectionCard>

        {/* Languages */}
        <SectionCard icon={<IconLanguage size={18} />} title={t('languages_spoken')}
          showValue={form.show_languages!} onShowChange={(val) => set('show_languages', val)}>
          <div className="flex flex-wrap gap-2">
            {LANGUAGES.map((lang) => {
              const selected = (form.languages_spoken || []).includes(lang.value)
              return (
                <button key={lang.value} type="button" onClick={() => toggleLanguage(lang.value)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                    selected ? 'bg-primary text-primary-foreground border-primary'
                      : 'bg-background text-muted-foreground border-border hover:border-primary/50'}`}>
                  {isAr ? lang.ar : lang.fr}
                </button>
              )
            })}
          </div>
        </SectionCard>

        <div className="flex justify-end pb-8">
          {(canEdit || (!profile && canCreate)) && (
            <Button onClick={handleSave} disabled={saving} size="lg">
              {saving ? t('saving') : t('save_profile')}
            </Button>
          )}
        </div>
      </div>

      <DeleteDialog
        open={deleteOpen} onOpenChange={() => setDeleteOpen(false)}
        onDeleted={() => {
          setProfile(null)
          setForm((prev) => ({ ...prev, is_published: false }))
          toast({ title: t('profile_deleted') })
          setDeleteOpen(false)
        }}
        getDeleteName={() => form.full_name || t('lawyer_profile')}
        deleteRow={delete_lawyer_profile} deleteTitle={t('delete_profile')}
      />

      <ConfirmDialog
        open={publishConfirm !== null} onOpenChange={() => setPublishConfirm(null)}
        title={publishConfirm ? t('confirm_publish_title') : t('confirm_unpublish_title')}
        desc={publishConfirm ? t('confirm_publish_desc') : t('confirm_unpublish_desc')}
        cancelBtnText={t('cancel')}
        confirmText={publishConfirm ? t('publish') : t('unpublish')}
        destructive={!publishConfirm}
        handleConfirm={() => { set('is_published', publishConfirm!); setPublishConfirm(null) }}
      />

      {previewing && (
        <LawyerProfilePreview form={form} photoUrl={profile?.photo_url}
          onClose={() => setPreviewing(false)}
          onSave={async () => { await handleSave(); setPreviewing(false) }}
          saving={saving} />
      )}
    </Main>
  )
}